/**
 * AddParticipantModal Component
 *
 * Modal for admin individual enrollment via PAPI user search.
 * Uses UserSearchSelect to find users and useEnrollUser to enroll them.
 */

import { useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import FormField from '@cloudscape-design/components/form-field';
import Link from '@cloudscape-design/components/link';
import { UserSearchSelect } from './UserSearchSelect';
import { UserSearchResult } from '../api/users';
import { useEnrollUser } from '../hooks/queries/useCohorts';
import { badgePhotoUrl, phoneToolUrl } from '../lib/externalUrls';

export interface AddParticipantModalProps {
  visible: boolean;
  cohortId: string;
  programId: string;
  onDismiss: () => void;
  onSuccess: () => void;
}

export function AddParticipantModal({
  visible,
  cohortId,
  programId,
  onDismiss,
  onSuccess,
}: AddParticipantModalProps) {
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchResetKey, setSearchResetKey] = useState(0);

  const enrollUser = useEnrollUser();

  const resetState = () => {
    setSelectedUser(null);
    setError(null);
    setSearchResetKey(k => k + 1);
  };

  const handleDismiss = () => {
    if (!enrollUser.isPending) {
      resetState();
      onDismiss();
    }
  };

  const handleUserSelect = (user: UserSearchResult) => {
    setSelectedUser(user);
    setError(null);
  };

  const handleUserClear = () => {
    setSelectedUser(null);
    setError(null);
  };

  const handleConfirm = () => {
    if (!selectedUser) return;

    enrollUser.mutate(
      { cohortId, userId: selectedUser.login, programId },
      {
        onSuccess: () => {
          resetState();
          onSuccess();
          onDismiss();
        },
        onError: (err: any) => {
          if (
            err.response?.statusCode === 409 ||
            err.message?.includes('already enrolled')
          ) {
            setError('This user is already enrolled in the cohort');
          } else {
            setError(err.message || 'Failed to enroll user');
          }
        },
      },
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Add participant"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={enrollUser.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={enrollUser.isPending}
              disabled={!selectedUser || enrollUser.isPending}
            >
              Add
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormField
          label="Search for user"
          description="Search by name or alias to add to this cohort"
        >
          <UserSearchSelect
            key={searchResetKey}
            onSelect={handleUserSelect}
            onClear={handleUserClear}
            placeholder="Search by name or alias..."
            disabled={enrollUser.isPending}
            clearOnSelect={false}
          />
        </FormField>

        {selectedUser && (
          <SpaceBetween size="xs">
            <Box fontWeight="bold">Selected:</Box>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', background: '#f2f3f3', borderRadius: '8px' }}>
              <img src={badgePhotoUrl(selectedUser.login)} alt={selectedUser.name} style={{ width: 40, height: 40, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Box fontWeight="bold">{selectedUser.name}</Box>
                <Box color="text-body-secondary" fontSize="body-s"><Link href={phoneToolUrl(selectedUser.login)} external fontSize="body-s">@{selectedUser.login}</Link> — {selectedUser.title}</Box>
              </div>
              <Button variant="icon" iconName="close" ariaLabel="Clear selection" onClick={resetState} />
            </div>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Modal>
  );
}
